import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { lastValueFrom } from 'rxjs';
import { AuthService } from 'src/app/shared/auth/services/auth.service';
import { Diretoria } from 'src/app/shared/models/diretoria/Diretoria';
import { Polo } from 'src/app/shared/models/polo/Polo';
import {
  DiagnosticoAprovacaoRevertidaItem,
  DiagnosticoAprovacaoRevertidaResult,
  ResumoDiagnosticoAprovacaoRevertida
} from 'src/app/shared/models/relatorio/diagnostico-aprovacao-revertida';
import { Perfil } from 'src/app/shared/enums/Perfis/Perfis';
import { UserLoginSed } from 'src/app/shared/models/sed/UserLogin';
import { ConvivaErrorHandlingService } from 'src/app/shared/services/convivaErrorHandlingService';
import { EscolaService } from 'src/app/shared/services/escola.service';
import { PsicologoService } from 'src/app/shared/services/psicologo.service';
import { RelatorioService } from 'src/app/shared/services/relatorio.service';
import { ToastrService } from 'ngx-toastr';
import { angularCommonImports } from 'src/app/shared/ui/angular-common';
import { convivaCommonImports } from 'src/app/shared/ui/conviva-common';
import { libsCommonImports } from 'src/app/shared/ui/libs-common';

@Component({
  selector: 'app-diagnostico-aprovacao-revertida',
  templateUrl: './diagnostico-aprovacao-revertida.component.html',
  styleUrls: ['./diagnostico-aprovacao-revertida.component.scss'],
  standalone: true,
  imports: [angularCommonImports, convivaCommonImports, libsCommonImports]
})
export class DiagnosticoAprovacaoRevertidaComponent implements OnInit {
  private authService = inject(AuthService);
  private spinnerService = inject(NgxSpinnerService);
  private ConvivaErrors = inject(ConvivaErrorHandlingService);
  private toastr = inject(ToastrService);
  private relatorioService = inject(RelatorioService);
  private psicologoService = inject(PsicologoService);
  private escolaService = inject(EscolaService);

  public userLogado: UserLoginSed | null = null;
  public polos: Polo[] = [];
  public diretorias: Diretoria[] = [];
  public candidatos: DiagnosticoAprovacaoRevertidaItem[] = [];
  public resumo: ResumoDiagnosticoAprovacaoRevertida | null = null;
  public showModalCorrecao = false;
  public pesquisou = false;

  public get isGestorConviva(): boolean {
    return this.userLogado?.codigoPerfil === Perfil.GestorConviva;
  }

  public form = new FormGroup({
    codigoPolo: new FormControl<number | null>(null, Validators.required),
    codigoDiretoria: new FormControl<number | null>(null, Validators.required),
    dataInicio: new FormControl<string>('2026-06-01', Validators.required),
    dataFim: new FormControl<string>('2026-06-30', Validators.required)
  });

  public colunasGrid = [
    { title: 'Jornada', field: 'codigoJornada', width: 100, hozAlign: 'center', headerHozAlign: 'center' },
    { title: 'Data', field: 'dataJornada', width: 110, hozAlign: 'center', headerHozAlign: 'center',
      formatter: (cell: any) => {
        const v = cell.getValue();
        if (!v) return '';
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('pt-BR');
      }
    },
    { title: 'URE', field: 'nomeDiretoria', minWidth: 140 },
    { title: 'Escola', field: 'nomeEscola', minWidth: 180 },
    { title: 'Atividade', field: 'codigoAtividade', width: 110, hozAlign: 'center', headerHozAlign: 'center' },
    { title: 'Título', field: 'titulo', minWidth: 180 },
    { title: 'Status atual', field: 'statusAtual', width: 110, hozAlign: 'center', headerHozAlign: 'center' },
    { title: 'Status log', field: 'statusNoLog', width: 110, hozAlign: 'center', headerHozAlign: 'center' },
    { title: 'Estrelas', field: 'estrelasNoLog', width: 90, hozAlign: 'center', headerHozAlign: 'center' },
    { title: 'Usuário', field: 'nomeUsuarioAvaliacao', minWidth: 160 },
    { title: 'Hipótese', field: 'hipotese', minWidth: 260 }
  ];

  async ngOnInit(): Promise<void> {
    this.userLogado = this.authService.getUser();
    if (this.userLogado?.codigoPolo) {
      this.form.patchValue({ codigoPolo: this.userLogado.codigoPolo });
    }
    await this.getPolos();
    if (this.form.value.codigoPolo) {
      await this.getDiretorias();
      this.selecionarAraraquaraSeExistir();
    }
  }

  public async getPolos(): Promise<void> {
    this.spinnerService.show();
    const response = await lastValueFrom(this.psicologoService.getPolos())
      .catch((err) => this.ConvivaErrors.handleResultError(err));
    this.spinnerService.hide();
    if (!response?.isSucess) {
      this.toastr.error(response?.message ?? 'Erro ao carregar polos.');
      return;
    }
    this.polos = response.data ?? [];
  }

  public async OnChangePolo(): Promise<void> {
    this.form.patchValue({ codigoDiretoria: null });
    this.diretorias = [];
    await this.getDiretorias();
    this.selecionarAraraquaraSeExistir();
  }

  public async getDiretorias(): Promise<void> {
    const codigoPolo = this.form.value.codigoPolo;
    if (!codigoPolo) return;

    this.spinnerService.show();
    const response = await lastValueFrom(this.psicologoService.getDiretoriaPorPolo(codigoPolo))
      .catch((err) => this.ConvivaErrors.handleResultError(err));
    if (!response?.isSucess) {
      this.spinnerService.hide();
      this.toastr.error(response?.message ?? 'Erro ao carregar UREs.');
      return;
    }

    const diretorias = (response.data ?? []) as Diretoria[];
    // GetDiretoriasPorPolo pode vir sem nomeDiretoria; completa via Hub.
    if (diretorias.some(d => !d.nomeDiretoria)) {
      const nomes = await lastValueFrom(this.escolaService.getDiretoriaPorTipoDiretoria())
        .catch((err) => this.ConvivaErrors.handleResultError(err));
      if (nomes?.isSucess && Array.isArray(nomes.data)) {
        const mapa = new Map<number, string>(
          (nomes.data as Diretoria[]).map(d => [d.codigoDiretoria, d.nomeDiretoria])
        );
        diretorias.forEach(d => {
          if (!d.nomeDiretoria) {
            d.nomeDiretoria = mapa.get(d.codigoDiretoria) ?? `URE ${d.codigoDiretoria}`;
          }
        });
      } else {
        diretorias.forEach(d => {
          if (!d.nomeDiretoria) {
            d.nomeDiretoria = `URE ${d.codigoDiretoria}`;
          }
        });
      }
    }

    this.diretorias = [...diretorias].sort((a, b) =>
      (a.nomeDiretoria ?? '').localeCompare(b.nomeDiretoria ?? '', 'pt-BR')
    );
    this.spinnerService.hide();
  }

  private selecionarAraraquaraSeExistir(): void {
    const araraquara = this.diretorias.find(d =>
      (d.nomeDiretoria ?? '').toUpperCase().includes('ARARAQUARA')
    );
    if (araraquara?.codigoDiretoria) {
      this.form.patchValue({ codigoDiretoria: araraquara.codigoDiretoria });
    }
  }

  private montarPayload() {
    const v = this.form.getRawValue();
    return {
      codigoPolo: v.codigoPolo,
      codigoDiretoria: v.codigoDiretoria,
      dataInicio: v.dataInicio,
      dataFim: v.dataFim
    };
  }

  public async pesquisar(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastr.warning('Informe Polo, URE e o período.');
      return;
    }

    this.spinnerService.show();
    const response = await lastValueFrom(
      this.relatorioService.getDiagnosticoAprovacaoRevertida(this.montarPayload())
    ).catch((err) => this.ConvivaErrors.handleResultError(err));
    this.spinnerService.hide();

    if (!response?.isSucess) {
      this.toastr.error(response?.message ?? 'Erro ao consultar diagnóstico.');
      return;
    }

    const data = (response.data ?? {}) as DiagnosticoAprovacaoRevertidaResult;
    this.resumo = data.resumo ?? null;
    this.candidatos = data.candidatos ?? [];
    this.pesquisou = true;

    if (!this.candidatos.length) {
      this.toastr.info('Nenhum candidato à correção encontrado para os filtros.');
    }
  }

  public abrirModalCorrecao(): void {
    if (!this.isGestorConviva) {
      this.toastr.warning('Apenas o perfil Gestor CONVIVA pode aplicar a correção.');
      return;
    }
    if (!this.candidatos.length) {
      this.toastr.warning('Não há registros para corrigir. Pesquise antes de aplicar.');
      return;
    }
    this.showModalCorrecao = true;
  }

  public fecharModalCorrecao(): void {
    this.showModalCorrecao = false;
  }

  public async confirmarCorrecao(): Promise<void> {
    if (!this.isGestorConviva) {
      this.toastr.warning('Apenas o perfil Gestor CONVIVA pode aplicar a correção.');
      return;
    }
    if (!this.userLogado || this.form.invalid) return;

    this.spinnerService.show();
    const payload = {
      ...this.montarPayload(),
      codigoUsuario: this.userLogado.codigoUsuario,
      nomeUsuario: this.userLogado.nome
    };

    const response = await lastValueFrom(
      this.relatorioService.setCorrigeAprovacaoRevertida(payload)
    ).catch((err) => this.ConvivaErrors.handleResultError(err));
    this.spinnerService.hide();

    if (!response?.isSucess) {
      this.toastr.error(response?.message ?? 'Erro ao aplicar correção.');
      return;
    }

    const msg = response.data?.mensagem ?? response.message ?? 'Correção aplicada.';
    this.toastr.success(msg);
    this.fecharModalCorrecao();
    await this.pesquisar();
  }
}
